import * as stylex from '@stylexjs/stylex';
import { useForm } from '@tanstack/react-form';
import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { type ReactElement, type ReactNode } from 'react';
import { MenuTrigger, Pressable } from 'react-aria-components';
import { pick as rPick, forEachObj as rForEachObj } from 'remeda';
import {
  number as vNumber,
  parse as vParse,
  pipe as vPipe,
  string as vString,
  transform as vTransform,
} from 'valibot';

import { SecondaryActionsIconButton } from '@/components/secondary-actions-icon-button.component';
import { SESSION_STORAGE } from '@/constants/storage';
import { theme } from '@/constants/theme';
import { useFormDraft } from '@/hooks/use-form-draft.hook';
import { CampaignRequestProgramStageEnum } from '@/libs/openapi';
import {
  TOAST_TIMEOUT,
  tryCatch,
  PROGRESS_INDICATOR_MIN_DELAY_DURATION,
  minDelay,
  Badge,
  ChevronDownSmallStrokeIcon,
  Menu,
  MenuItem,
  Popover,
  Text,
  Breadcrumbs,
  Breadcrumb,
} from '@/libs/serenity-ui';
import { RouterLink } from '@/libs/tanstack-router';
import { auditLogQueries } from '@/resources/audit-log/audit-log.api';
import { campaignRequestProgramMutations, campaignRequestProgramQueries } from '@/resources/campaign-request-program/campaign-request-program.api';
import { campaignRequestProgramPolicies } from '@/resources/campaign-request-program/campaign-request-program.policies';
import { ProgramForm, type ProgramFormValues } from '@/routes/_main/_form/programs/-components/campaign-request-program-form.component';
import { Resource } from '@/types/resource.type';
import { formatEnumValue } from '@/utils/format.util';
import { timezone } from '@/utils/timezone.util';

export const Route = createFileRoute('/_main/_form/programs/$programId/edit/')({
  component: RouteComponent,
  params: {
    parse: ({ programId }) => ({ programId: vParse(vPipe(vString(), vTransform(Number), vNumber()), programId) }),
    stringify: ({ programId }) => ({ programId: String(programId) }),
  },
  staticData: {
    form: {
      heading: 'Edit',
      toolbarLayout: 'full-width',
      goBackFallbackPath: '/programs',
      slots: {
        topBarComponent: TopBar,
        statusComponent: StatusComponent,
        actionsComponent: ActionsComponent,
      },
    },
  },
  beforeLoad: async ({ context, params }) => {
    if (context.user && !context.programsFeatureEnabled) {
      throw redirect({ to: '/campaigns' });
    }

    if (context.user && !context.can.campaignRequestPrograms.view()) {
      throw redirect({ to: '/' });
    }

    const boundPolicies = campaignRequestProgramPolicies.bindContext({ user: context.user.original, organization: context.organization?.original });

    const detail = await context.queryClient.ensureQueryData(campaignRequestProgramQueries.detail(params.programId));

    if (!boundPolicies.update(detail)) {
      throw redirect({ to: '/programs/$programId', params: { programId: params.programId }});
    }

    return {
      can: {
        [Resource.campaignRequestProgram as const]: rPick(boundPolicies, [ 'create', 'update' ]),
      },
      queryOptions: {
        detail: campaignRequestProgramQueries.detail(params.programId),
      },
    };
  },
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(context.queryOptions.detail);
  },
  head: () => ({
    meta: [{ title: 'TrendMD | Edit program' }],
  }),
});

function RouteComponent(): ReactElement {
  const navigate = Route.useNavigate();
  const routeParams = Route.useParams();
  const routeContext = Route.useRouteContext();

  const detailQuery = useSuspenseQuery(routeContext.queryOptions.detail);
  const updateMutation = useMutation(campaignRequestProgramMutations.update);

  const form = useForm({
    defaultValues: {
      name: detailQuery.data.original.name ?? '',
      agency: detailQuery.data.original.agency ?? '',
      timezone: detailQuery.data.original.timezone ?? timezone.get(),
      startDate: detailQuery.data.meta.startDateRelativeToTimezone?.forFormValue ?? detailQuery.data.original.startDate,
      endDate: detailQuery.data.meta.endDateRelativeToTimezone?.forFormValue ?? detailQuery.data.original.endDate,
      cost: detailQuery.data.transformed.forForm.cost != null ? detailQuery.data.transformed.forForm.cost : undefined,
      totalBudget: detailQuery.data.transformed.forForm.totalBudget != null ? detailQuery.data.transformed.forForm.totalBudget : undefined,
      monthlyBudget: detailQuery.data.transformed.forForm.monthlyBudget != null
        ? Object.fromEntries(Object.entries(detailQuery.data.transformed.forForm.monthlyBudget).map(([ k, v ]) => [ k, Number(v) ]))
        : {},
      clickGoal: detailQuery.data.original.clickGoal ?? undefined,
      targeting: detailQuery.data.original.targeting ?? '',
      ownerUserId: detailQuery.data.original.owner?.id ?? undefined,
      ownerSearchInput: detailQuery.data.meta.ownerFullName ?? '',
    } as ProgramFormValues,
    onSubmit: async ({ value }) => {
      const monthlyBudgetCents = Object.fromEntries(Object.entries(value.monthlyBudget).map(([ k, v ]) => [ k, Math.round(Number(v) * 100) ]));

      const updateResponse = await tryCatch(minDelay(updateMutation.mutateAsync({
        id: routeParams.programId,
        body: {
          name: value.name,
          agency: value.agency || undefined,
          startDate: value.startDate!,
          endDate: value.endDate!,
          timezone: value.timezone,
          cost: Math.round((value.cost ?? 0) * 100),
          totalBudget: Math.round((value.totalBudget ?? 0) * 100),
          monthlyBudget: monthlyBudgetCents,
          targeting: value.targeting || undefined,
          ownerUserId: value.ownerUserId,
        },
      }), PROGRESS_INDICATOR_MIN_DELAY_DURATION));

      if (updateResponse.error) {
        routeContext.toastQueue.add(
          { message: updateResponse.error.message, variant: 'error' },
          { timeout: TOAST_TIMEOUT },
        );
        return;
      }

      clearDraft();
      routeContext.queryClient.invalidateQueries(campaignRequestProgramQueries.all);
      routeContext.queryClient.invalidateQueries(auditLogQueries.all);

      navigate({ to: '/programs/$programId', params: { programId: routeParams.programId }});
    },
  });

  const { hasDraft, restoreDraft, clearDraft, cancelDraft } = useFormDraft<ProgramFormValues>({
    storageKey: SESSION_STORAGE.campaignRequestProgramEditDraft(routeParams.programId).key,
    subscribe: (cb) => form.store.subscribe(({ currentVal }) => !currentVal.isDefaultValue && cb(currentVal.values)),
    restoreValues: rForEachObj((value, key) => form.setFieldValue(key, value)),
  });

  return (
    <ProgramForm
      canUpdate={routeContext.can.campaignRequestProgram.update(detailQuery.data.original)}
      cancelDraft={cancelDraft}
      form={form}
      hasDraft={hasDraft}
      restoreDraft={restoreDraft}
      submitLabel="Save"
      validationExcludeId={routeParams.programId}
    />
  );
}

function StatusComponent(): ReactNode {
  const routeContext = Route.useRouteContext();
  const routeParams = Route.useParams();
  const detailQuery = useSuspenseQuery(routeContext.queryOptions.detail);

  const canUpdate = routeContext.can.campaignRequestProgram.update(detailQuery.data.original);

  const stageMutation = useMutation(campaignRequestProgramMutations.changeStage);

  if (!(canUpdate && detailQuery.data.original.stageTransitions?.length)) {
    return (
      <Badge {...theme.badge[detailQuery.data.transformed.forLabels.stage]} isRounded size={32}>
        <Text>{detailQuery.data.transformed.forLabels.stage}</Text>
      </Badge>
    );
  }

  return (
    <MenuTrigger>
      <Pressable>
        <Badge role="button" style={badgeStyles.interactive} {...theme.badge[detailQuery.data.transformed.forLabels.stage]} isRounded size={32}>
          <Text>{detailQuery.data.transformed.forLabels.stage}</Text>
          <ChevronDownSmallStrokeIcon color={theme.badge[detailQuery.data.transformed.forLabels.stage].color} size={20} slot={null!} />
        </Badge>
      </Pressable>
      <Popover placement="bottom start">
        <Menu
          disabledKeys={stageMutation.isPending ? detailQuery.data.original.stageTransitions.map(String) : []}
          onAction={(key) => stageMutation.mutate( { id: routeParams.programId, body: { stage: Number(key) }})}>
          {detailQuery.data.original.stageTransitions.map((targetStage) => (
            <MenuItem id={String(targetStage)} key={targetStage}>
              <Text>{formatEnumValue(CampaignRequestProgramStageEnum)(targetStage)}</Text>
            </MenuItem>
          ))}
        </Menu>
      </Popover>
    </MenuTrigger>
  );
}

const badgeStyles = stylex.create({
  interactive: {
    cursor: 'pointer',
  },
});

function ActionsComponent(): ReactElement {
  return <SecondaryActionsIconButton />;
}

function TopBar () {
  const routeParams = Route.useParams();
  const detailQuery = useQuery(campaignRequestProgramQueries.detail(routeParams.programId));

  return (
    <Breadcrumbs>
      <Breadcrumb>
        <RouterLink to="/programs">Programs</RouterLink>
      </Breadcrumb>
      <Breadcrumb>
        <RouterLink params={routeParams} style={breadcrumbStyles.dynamic} to="/programs/$programId">{detailQuery.data?.original.name}</RouterLink>
      </Breadcrumb>
      <Breadcrumb hidden>
        <RouterLink params={routeParams} to="/programs/$programId/edit">Edit</RouterLink>
      </Breadcrumb>
    </Breadcrumbs>
  );
}

const breadcrumbStyles = stylex.create({
  dynamic: {
    display: 'block',
    maxWidth: 200,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});
